/**
 * InputsMarketProvider keeps state of market results
 */
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isShallowEqual } from 'utils/arrays.utils';
import { DEFAULT_PAGE_NUMBER } from 'utils/constants';
import { InputsMarketResultsQuery, PageInfo, useInputsMarketResultsQuery } from 'generated/graphql';
import useInputsMarketQueryPaused from '../hooks/useInputsMarketQueryPaused';
import useStitchInputsMarket from '../hooks/useStitchInputMarket';
import { useInputsState } from './InputsStateProvider';

// The context carries the sections the inputs market query returns; the
// generated InputsMarketSection asks for fields the query doesn't select.
export type InputsMarketSectionData = NonNullable<
  NonNullable<InputsMarketResultsQuery['inputsMarket']>['results']
>['sections'][number];

interface InputsMarketContextProps {
  fetchingState: {
    refreshMarketResult: () => void;
    isFetching: boolean;
    isInitialFetch: boolean;
  };
  sections: InputsMarketSectionData[];
  pageInfo: PageInfo | null;
  onResetState: () => void;
}

export const Context = createContext<InputsMarketContextProps | undefined>(undefined);

export const InputsMarketProvider = ({ children }: { children: ReactNode }) => {
  const { queryInput } = useInputsState();

  const isQueryPaused = useInputsMarketQueryPaused();
  const [marketResult, refreshMarketResult] = useInputsMarketResultsQuery({
    variables: {
      input: queryInput,
    },
    pause: isQueryPaused,
  });

  const { marketQuery: stitchedMarket, setMarketQuery } = useStitchInputsMarket(marketResult.data);

  const isFetching = marketResult.fetching;

  /** We need to handle resetting the stitched value when filter changes so that loading state shows appropriately */
  const [prevFilters, setPrevFilters] = useState(queryInput.filters || []);
  const handleResetState = useCallback(() => {
    setMarketQuery(undefined);
  }, [setMarketQuery]);

  useEffect(() => {
    if (!isShallowEqual(queryInput.filters, prevFilters)) {
      handleResetState();
      setPrevFilters(queryInput.filters || []);
    }
  }, [queryInput.filters, prevFilters, handleResetState]);

  useEffect(() => {
    if (queryInput.search) {
      handleResetState();
    }
  }, [queryInput.search, handleResetState]);

  const value = useMemo(
    () => ({
      fetchingState: {
        refreshMarketResult,
        isFetching,
        isInitialFetch:
          isFetching &&
          queryInput.paginationOption.pageNumber === DEFAULT_PAGE_NUMBER &&
          !stitchedMarket?.inputsMarket?.results?.sections?.length,
      },
      sections: stitchedMarket?.inputsMarket?.results?.sections ?? [],
      pageInfo: stitchedMarket?.inputsMarket?.results?.page ?? null,
      onResetState: handleResetState,
    }),
    [
      isFetching,
      stitchedMarket?.inputsMarket?.results?.sections,
      stitchedMarket?.inputsMarket?.results?.page,
      queryInput.paginationOption.pageNumber,
      refreshMarketResult,
      handleResetState,
    ],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useInputsMarket = () => {
  const result = useContext(Context);
  if (!result) throw new Error('useInputsMarket must be used within a InputsMarketProvider');
  return result;
};
